// Local-mode mocks for an endpoint's Local users (POST /instances/localUsers)
// and per-account password rotation (POST /passwords/setAccountRotation).
// Stateful in memory per instanceId until reload, seeded on first read:
//   root    — locked (passwordState L), no rotation
//   ubuntu  — password set, rotated on its own every 30 days, revealable
//   deploy  — password set, rotated by the shared profile "Linux servers"
//   oracle  — no password (NP)
//   backup  — password set, own rotation every 7 days, last rotation failed
//   nagios  — password state not reported by the scan (""), no rotation
//   alice   — added by Authnull, logs in with the key, expires in 3 days
// Added users come from managedAccountsMocks.js, so Add user / Remove user
// there show up here (pending → active, removing → gone, about 2 reads each).
// Turning rotation on runs a rotation for one read, then sets rotatedAt now.
// db-01.prod (2) has an older setup: canAddPermanent is false there.
// /instances/adminCredentialStatus is mocked elsewhere (ADMIN_MOCKS).

import { readManagedAccounts, seedManagedAccount } from "./managedAccountsMocks";

const MIN = 60e3;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;
const ago = (ms) => new Date(Date.now() - ms).toISOString();
const ahead = (ms) => new Date(Date.now() + ms).toISOString();
const now = () => new Date().toISOString();

const DEFAULT_INTERVAL_DAYS = 30;
const OLD_SETUP = new Set([2]);
const helperOf = (id) => (OLD_SETUP.has(id) ? 7 : 8);

// instanceId → { accounts: { name → { passwordState, inUse, revealable, rotation } } }
const endpoints = {};

const off = (extra) => ({ enabled: false, own: false, jobRunning: false, ...extra });

function seed(id) {
  if (endpoints[id]) return endpoints[id];
  const ep = {
    accounts: {
      root: { passwordState: "L", inUse: "key", revealable: false, rotation: off() },
      // The sudo account the endpoint was onboarded with (admin: true).
      sysadmin: { admin: true, passwordState: "P", inUse: "key", revealable: false, rotation: off() },
      ubuntu: {
        passwordState: "P", inUse: "password", revealable: true,
        rotation: {
          enabled: true, own: true, intervalDays: 30, status: "managed", jobRunning: false,
          rotatedAt: ago(4 * DAY), nextDueAt: ahead(26 * DAY),
        },
      },
      deploy: {
        passwordState: "P", inUse: "password", revealable: true,
        rotation: {
          enabled: true, own: false, profile: "Linux servers", status: "managed", jobRunning: false,
          rotatedAt: ago(10 * DAY), nextDueAt: ahead(20 * DAY),
        },
      },
      oracle: { passwordState: "NP", inUse: "key", revealable: false, rotation: off() },
      nagios: { passwordState: "", inUse: "key", revealable: false, rotation: off() },
      backup: {
        passwordState: "P", inUse: "password", revealable: true,
        rotation: {
          enabled: true, own: true, intervalDays: 7, status: "failed", jobRunning: false,
          detail: "passwd: Authentication token manipulation error",
          rotatedAt: ago(9 * DAY), nextDueAt: ago(2 * DAY),
        },
      },
    },
  };
  endpoints[id] = ep;
  seedManagedAccount({
    endpointId: id, account: "alice", credential: "key", status: "active", note: "Short-term access",
    expiresAt: ahead(3 * DAY), createdAt: ago(2 * HOUR), activeAt: ago(2 * HOUR - MIN),
  });
  return ep;
}

// An added user's own entry, made when it first shows up: a password account
// is rotated on its own schedule from when it went active.
function addedEntry(m) {
  if (m.credential !== "password") return { passwordState: "L", inUse: "key", revealable: false, rotation: off() };
  if (m.status !== "active") {
    return { passwordState: "", inUse: "key", revealable: false, rotation: off({ intervalDays: m.intervalDays }) };
  }
  const at = m.activeAt || now();
  return {
    passwordState: "P", inUse: "password", revealable: true,
    rotation: {
      enabled: true, own: true, intervalDays: m.intervalDays || DEFAULT_INTERVAL_DAYS, status: "managed",
      jobRunning: false, rotatedAt: at, nextDueAt: new Date(new Date(at).getTime() + (m.intervalDays || 30) * DAY).toISOString(),
    },
  };
}

// A rotation turned on runs for one read, then has rotated.
function advanceRotation(entry) {
  const r = entry.rotation;
  if (r._left === undefined) return;
  r._left -= 1;
  if (r._left > 0) return;
  delete r._left;
  Object.assign(r, {
    jobRunning: false, status: "managed", detail: undefined, rotatedAt: now(),
    nextDueAt: ahead((r.intervalDays || DEFAULT_INTERVAL_DAYS) * DAY),
  });
  Object.assign(entry, { passwordState: "P", inUse: "password", revealable: true });
}

const rotationView = (r) => {
  const out = { ...r };
  delete out._left;
  Object.keys(out).forEach((k) => out[k] === undefined && delete out[k]);
  return out;
};

export const LOCAL_USERS_MOCKS = [
  {
    pattern: "/instances/localusers",
    respond: (body) => {
      const id = Number(body?.instanceId) || 0;
      const ep = seed(id);
      const managed = readManagedAccounts(id).filter((m) => m.status !== "removed" && m.status !== "failed");
      const users = {};
      Object.entries(ep.accounts).forEach(([account, e]) => {
        if (e.added) return;
        advanceRotation(e);
        users[account] = { account, passwordState: e.passwordState, added: false, ...(e.admin ? { admin: true } : {}), inUse: e.inUse, revealable: e.revealable, rotation: rotationView(e.rotation) };
      });
      managed.forEach((m) => {
        // Re-made until active, so a password account starts rotating then.
        let e = ep.accounts[m.account];
        if (!e || (e.added && e.status !== m.status && m.status === "active")) {
          e = { ...addedEntry(m), added: true, status: m.status };
          ep.accounts[m.account] = e;
        }
        e.status = m.status;
        advanceRotation(e);
        users[m.account] = {
          account: m.account, passwordState: e.passwordState, added: true, managed: m,
          inUse: e.inUse, revealable: e.revealable, rotation: rotationView(e.rotation),
        };
      });
      // Gone from the machine: forget their rotation too.
      Object.keys(ep.accounts).forEach((a) => {
        if (ep.accounts[a].added && !users[a]) delete ep.accounts[a];
      });
      const list = Object.values(users).sort((x, y) =>
        (x.account === "root") !== (y.account === "root") ? (x.account === "root" ? -1 : 1) : x.account.localeCompare(y.account),
      );
      return {
        users: list,
        helperVersion: helperOf(id),
        canAddPermanent: helperOf(id) >= 8,
        defaultIntervalDays: DEFAULT_INTERVAL_DAYS,
        minHours: 1,
        maxDays: 90,
      };
    },
  },
  {
    // { instanceId, account, enabled, intervalDays } → { rotation }
    pattern: "/passwords/setaccountrotation",
    respond: (body) => {
      const id = Number(body?.instanceId) || 0;
      const account = String(body?.account || "").trim();
      const enabled = !!body?.enabled;
      const intervalDays = Number(body?.intervalDays) || DEFAULT_INTERVAL_DAYS;
      const ep = seed(id);
      const e = ep.accounts[account] || (ep.accounts[account] = { passwordState: "", inUse: "key", revealable: false, rotation: off() });
      e.rotation = enabled
        ? { ...e.rotation, enabled: true, own: true, profile: undefined, intervalDays, status: "pending", detail: undefined, jobRunning: true, nextDueAt: now(), _left: 2 }
        : { ...e.rotation, enabled: false, own: false, profile: undefined, intervalDays, status: undefined, detail: undefined, jobRunning: false, nextDueAt: undefined };
      if (!enabled) delete e.rotation._left;
      return { rotation: { endpointId: id, account, enabled, intervalDays } };
    },
  },
];
